
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ButtonBlockConfiguration } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  BackgroundControls,
  BorderControls,
  ResponsiveControls,
} from "../shared";

interface ButtonBlockPropertiesProps {
  config: ButtonBlockConfiguration;
  onChange: (config: ButtonBlockConfiguration) => void;
}

/**
 * ButtonBlockProperties Component
 * 
 * Property panel for button blocks with text, link, and styling controls.
 * Includes a live preview of the button at the top.
 * 
 * @param config - Current button block configuration
 * @param onChange - Callback when configuration changes
 */
export function ButtonBlockProperties({ config, onChange }: ButtonBlockPropertiesProps) {
  const updateField = <K extends keyof ButtonBlockConfiguration>(
    field: K,
    value: ButtonBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const borderStyle = config.border.width > 0
    ? `${config.border.width}px ${config.border.style} ${config.border.color}`
    : 'none';

  return (
    <div className="space-y-3">
      {/* Live Preview */}
      <div className="flex justify-center p-4 rounded-md bg-muted">
        <div
          style={{
            display: 'inline-block',
            backgroundColor: config.background.color || '#3b82f6',
            color: config.typography.color || '#ffffff',
            fontFamily: config.typography.fontFamily,
            fontSize: `${config.typography.fontSize}px`,
            fontWeight: config.typography.fontWeight,
            fontStyle: config.typography.italic ? 'italic' : 'normal',
            textDecoration: config.typography.underline
              ? 'underline'
              : config.typography.strikethrough
                ? 'line-through'
                : 'none',
            textTransform: config.typography.textTransform !== 'none'
              ? config.typography.textTransform
              : undefined,
            letterSpacing: config.typography.letterSpacing
              ? `${config.typography.letterSpacing}px`
              : undefined,
            lineHeight: config.typography.lineHeight,
            paddingTop: `${config.padding.top}px`,
            paddingRight: `${config.padding.right}px`,
            paddingBottom: `${config.padding.bottom}px`,
            paddingLeft: `${config.padding.left}px`,
            borderRadius: `${config.border.radius}px`,
            border: borderStyle,
            width: config.width === 'full' ? '100%' : undefined,
            textAlign: 'center',
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          {config.text || 'Button text'}
        </div>
      </div>

      {/* Primary Fields */}
      <div>
        <Label className="text-xs font-medium mb-1 block">
          Button Text <span className="text-destructive">*</span>
        </Label>
        <Input
          type="text"
          value={config.text}
          onChange={(e) => updateField('text', e.target.value)}
          placeholder="Get started"
          maxLength={100}
          required
        />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">
          Link URL <span className="text-destructive">*</span>
        </Label>
        <Input
          type="url"
          value={config.href}
          onChange={(e) => updateField('href', e.target.value)}
          placeholder="https://example.com"
          required
        />
      </div>

      {/* Button Width */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Button Width</Label>
        <Select
          value={config.width}
          onChange={(e) => updateField('width', e.target.value as 'auto' | 'full')}
        >
          <option value="auto">Auto</option>
          <option value="full">Full Width</option>
        </Select>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["typography", "background"]} className="w-full">
        
        {/* Typography Section */}
        <AccordionItem value="typography">
          <AccordionTrigger>Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.typography}
              onChange={(typography) => updateField('typography', typography)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Background Section */}
        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent>
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Border Section */}
        <AccordionItem value="border">
          <AccordionTrigger>Border & Radius</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Spacing Section */}
        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Alignment Section */}
        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Responsive Section */}
        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
